import { useEffect, useRef, useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import {
  DefaultEntityFilters,
  EntityFilter,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { CatalogFilterBlueprint } from '@backstage/plugin-catalog-react/alpha';

type GroupsFilters = DefaultEntityFilters & {
  groupOwnership?: GroupOwnershipEntityFilter;
};

/**
 * Policy X-Via: o usuário logado só enxerga entidades dos grupos do GitLab aos
 * quais pertence. Exceções: templates universais (annotation
 * `xvia.backstage.io/universal-template`), Users e Groups.
 */
class GroupOwnershipEntityFilter implements EntityFilter {
  constructor(private readonly allowedGroups: string[]) {}

  filterEntity(entity: Entity): boolean {
    const isUniversalTemplate =
      entity.metadata?.annotations?.['xvia.backstage.io/universal-template'] === 'true';

    if (isUniversalTemplate) {
      return true;
    }

    const kind = entity.kind?.toLowerCase();

    if (kind === 'group') {
      const groupName = entity.metadata?.name?.toLowerCase() ?? '';
      if (groupName.includes('deletion_scheduled')) {
        return false;
      }
    }

    if (kind === 'user' || kind === 'group') {
      return true;
    }

    if (!this.allowedGroups || this.allowedGroups.length === 0) {
      return true;
    }

    const entityOwner = entity.spec?.owner?.toString() ?? '';

    if (!entityOwner) {
      return false;
    }

    return this.allowedGroups.some(allowedGroup => {
      const normalizedOwner = entityOwner.toLowerCase();
      const normalizedAllowed = allowedGroup.toLowerCase();

      return (
        normalizedOwner === normalizedAllowed ||
        normalizedOwner.endsWith(`/${normalizedAllowed.split('/').pop()}`)
      );
    });
  }

  getCatalogFilters(): Record<string, string | string[]> {
    return {};
  }
}

/**
 * Componente sem UI: apenas aplica o GroupOwnershipEntityFilter à lista de
 * entidades. Os pickers visíveis (Kind, Type, Owner, etc.) são os filtros
 * padrão da página nova do catálogo.
 */
const GroupOwnershipFilter = () => {
  const [userOwnershipEntities, setUserOwnershipEntities] = useState<string[]>([]);
  const identityApi = useApi(identityApiRef);
  const { filters, updateFilters } = useEntityList<GroupsFilters>();
  const lastAppliedFilterRef = useRef<string>('');

  useEffect(() => {
    identityApi.getBackstageIdentity().then(identity => {
      const ownershipEntities = identity.ownershipEntityRefs ?? [];
      setUserOwnershipEntities(ownershipEntities);
    });
  }, [identityApi]);

  useEffect(() => {
    const currentFilterKey = `auto:${JSON.stringify(userOwnershipEntities)}`;

    if (lastAppliedFilterRef.current === currentFilterKey) {
      return;
    }

    lastAppliedFilterRef.current = currentFilterKey;

    if (userOwnershipEntities.length > 0) {
      updateFilters({
        groupOwnership: new GroupOwnershipEntityFilter(userOwnershipEntities),
      });
    } else {
      if (filters.groupOwnership) {
        updateFilters({
          groupOwnership: undefined,
        });
      }
    }
  }, [filters.groupOwnership, userOwnershipEntities, updateFilters]);

  return null;
};

export const groupOwnershipCatalogFilter = CatalogFilterBlueprint.make({
  name: 'group-ownership',
  params: {
    loader: async () => <GroupOwnershipFilter />,
  },
});
